import React from "react";
import { useDropzone } from "react-dropzone";
import { Download, Upload } from "lucide-react";
import type { Design } from "pages/creative/pageV2/useDesignManagement";
import { useDesignUpload } from "campaign-builder/design/useDesignUpload";
import type { ChannelConfig } from "campaign-builder/config";
import { ReviewUploadModal } from "./ReviewUploadModal";

interface UploadPaneProps {
  designConfig: ChannelConfig["design"];
  format: string;
  formatLabel?: string;
  campaignId?: string;
  clientId?: string | null;
  templateUrl?: string;
  templateLabel?: string;
  onCommitted: (design: Design) => void;
}

export const UploadPane: React.FC<UploadPaneProps> = ({
  designConfig,
  format,
  formatLabel,
  campaignId,
  clientId,
  templateUrl,
  templateLabel,
  onCommitted,
}) => {
  const upload = useDesignUpload({ design: designConfig, format, campaignId, clientId });

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: upload.onDrop,
    onDropRejected: upload.onDropRejected,
    accept: designConfig.acceptedFileTypes,
    maxSize: upload.maxBytes,
    multiple: false,
    noClick: true,
  });

  const handleSaveAndContinue = async () => {
    const design = await upload.commitUpload();
    if (design) onCommitted(design);
  };

  const handleUploadNew = () => {
    upload.reset();
    open();
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <p className="text-foreground text-sm font-medium leading-5">Upload design</p>
        {templateUrl && (
          <a
            href={templateUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex shrink-0 items-center gap-1.5 text-[13px] font-medium text-foreground underline"
          >
            <Download className="h-3.5 w-3.5" />
            Download {templateLabel} template
          </a>
        )}
      </div>

      <div
        {...getRootProps()}
        className={`flex flex-col items-center gap-2 rounded-lg border border-dashed bg-background px-6 py-10 text-center ${
          upload.error ? "border-destructive" : "border-slate-300 dark:border-accent"
        }`}
      >
        <input {...getInputProps()} />
        <div className="bg-muted text-slate-600 dark:text-slate-300 flex h-10 w-10 items-center justify-center rounded-lg">
          <Upload className="h-5 w-5" />
        </div>
        <p className="text-foreground text-sm">
          {isDragActive ? (
            "Drop your PDF to upload"
          ) : (
            <>
              Drop your PDF here, or{" "}
              <button type="button" onClick={open} className="text-foreground font-medium underline">
                browse files
              </button>
            </>
          )}
        </p>
        <p className="text-muted-foreground text-xs">
          PDF{formatLabel ? ` · ${formatLabel}` : ""} · up to {designConfig.maxFileSizeMb} MB · front and back in one
          file
        </p>
      </div>

      {upload.error && <p className="text-sm text-destructive">{upload.error}</p>}

      <div className="text-muted-foreground mt-2 flex flex-col gap-1 text-xs leading-4">
        <p>Upload flyer design in PDF format. PDF file must:</p>
        <ul className="flex flex-col gap-1 pl-4">
          <li className="list-disc">Be in CMYK color profile</li>
          <li className="list-disc">Contain both front &amp; back sides of the flyer</li>
          <li className="list-disc">Contain bleed line, indicating where the edges are cut.</li>
        </ul>
      </div>

      <ReviewUploadModal
        open={upload.isRendering || (!!upload.preview && (upload.status === "ready" || upload.status === "committing"))}
        pages={upload.preview?.pages ?? []}
        format={format}
        isRendering={upload.isRendering}
        isCommitting={upload.isCommitting}
        onSaveAndContinue={handleSaveAndContinue}
        onUploadNew={handleUploadNew}
        onClose={() => {
          if (!upload.isCommitting) upload.reset();
        }}
      />
    </div>
  );
};
